import { useMemo } from 'react'
import { gql } from '@apollo/client'
import { Maybe } from '@graphql-tools/utils'
import { isAfter, parseISO } from 'date-fns'
import { ContentAvailabilityFragment } from '@/types/codegen-federation'

export const CONTENT_AVAIL_FRAG = gql`
  fragment ContentAvailability on ContentWatchableAvailability {
    watchableAt
    watchableAvailabilityStatus
    actionsToWatch {
      __typename
      ... on GuildAvailabilityAction {
        startDatetime
      }
    }
  }
`

interface UseContentAvailabilityResponse {
  canWatchNow: boolean
  canWatchNowIfGuildUpsell: boolean
  watchableAt: string | null
  canGuildUpsell: boolean
  guildWatchableAt: string | null
}

export const useContentAvailability = (content: Maybe<ContentAvailabilityFragment>): UseContentAvailabilityResponse => {
  return useMemo(() => {
    return getContentAvailability(content)
  }, [content])
}

export const getContentAvailability = (content: Maybe<ContentAvailabilityFragment>): UseContentAvailabilityResponse => {
  if (!content) {
    return {
      canWatchNow: false,
      canWatchNowIfGuildUpsell: false,
      watchableAt: null,
      canGuildUpsell: false,
      guildWatchableAt: null,
    }
  }
  const { watchableAt, watchableAvailabilityStatus, actionsToWatch } = content
  const canWatchNow = watchableAvailabilityStatus === 'WATCHABLE'

  const initWatchInfo = {
    canGuildUpsell: false,
    canWatchNowIfGuildUpsell: false,
    guildWatchableAt: null,
  }
  const { canGuildUpsell, canWatchNowIfGuildUpsell, guildWatchableAt } =
    actionsToWatch?.reduce((acc, action) => {
      if (canWatchNow) {
        return acc
      }
      if (action?.__typename === 'GuildAvailabilityAction') {
        acc.canGuildUpsell = true
        acc.guildWatchableAt = action.startDatetime
        if (acc.guildWatchableAt && isAfter(new Date(), parseISO(acc.guildWatchableAt))) {
          acc.canWatchNowIfGuildUpsell = true
        }
      }
      return acc
    }, initWatchInfo) ?? initWatchInfo

  return {
    canWatchNow,
    canWatchNowIfGuildUpsell,
    watchableAt,
    canGuildUpsell,
    guildWatchableAt,
  }
}
